import { Tree } from 'primereact/tree';
import React, { useEffect, useState } from 'react';
import { TreeItemDto } from '../../dtos/roleTree/TreeItemDto';
import {enterpriseTree, professionalTree, standardTree, treeForHost,treeForOthers} from "../../data/Permission";
import { createPermissionByRoleId, createPermissionByUserId, getPermissionByUserAndRole } from '../../service/permission/permissionService';
import { useDispatch, useSelector } from 'react-redux';
import { CreatePermissionByRoleInputDto } from '../../dtos/role/CreatePermissionByRoleInputDto';
import { CreatePermissionInputDto } from '../../dtos/role/CreatePermissionInputDto';
import { setPermissions } from '../../store/slices/permissionSlice';
import { setError } from '../../store/slices/errorSlice';
import { PermissionVariables } from '../../shared/constant/PermissionVariables';
import { useNavigate } from 'react-router-dom';
import { CreatePermissionByUserInputDto } from '../../dtos/role/CreatePermissionByUserIdInputDto';
import { Card } from '../ui/Card';
import { showToast } from '../../store/slices/toastSlice';
import { getValidIntegerOrNull } from '../../utils/storageUtils';

interface PermissionCheckState {
  [key: string]: {
    checked: boolean;
    partialChecked: boolean;
  };
}

interface PermissionProps {
  selectedKeys: PermissionCheckState
  setSelectedKeys: (keys: PermissionCheckState) => void,
  componentInfo: string;
  isSaveClicked: boolean;
  resetDialog: () => void;
  roleInfo: { name: string, displayName: string, id: number | null, tenantId?: number | null };
  allPermissions: any[],
}

const Permission: React.FC<PermissionProps> = ({
  selectedKeys,
  setSelectedKeys,
  componentInfo,
  resetDialog,
  roleInfo,
  isSaveClicked,
  allPermissions
}) => {
  const emptyRole = {
    Name: "",
    Description: "",
  };
  const [tree, setTree] = useState<any>([]);
  const [expandedKeys, setExpandedKeys] = useState<{ [key: string]: boolean }>(
    {}
  );
  const tenantId = getValidIntegerOrNull("tenantId");

  const [previouslySelectedNodes, setPreviouslySelectedNodes] = useState<
    PermissionCheckState | {}
  >({});
  const [allUserPermissions, setAllUserPermissions] = useState<
    { [key: string]: { checked: boolean; partialChecked: boolean } } | {}
  >({});
  const [permissionsInDB, setPermissionsInDB] = useState<any>([]);
  const dispatch = useDispatch();
  const navigate = useNavigate();
    const edition = useSelector((state: any) => state.edition.editionId);
  // Function to collect all node keys for expansion
  const expandAllNodes = (nodes: TreeItemDto[]) => {
    let keys: { [key: string]: boolean } = {};
    const traverse = (nodeArray: TreeItemDto[]) => {
      nodeArray.forEach((node) => {
        keys[node.key] = true; // Mark the node as expanded
        if (node.children) {
          traverse(node.children); // Recursively collect keys from children
        }
      });
    };
    traverse(nodes);
    return keys;
  };

  const getPermission = async () => {
    if (Object.keys(selectedKeys)?.includes(PermissionVariables.role)) {
      const userId = localStorage.getItem('userId');
      const tenantId = getValidIntegerOrNull("tenantId");
      const roleIds = localStorage.getItem('roleIds') || '[]';
      if (userId && roleIds?.length !== 0) {
        const permissionInputs = {
          userId: JSON.parse(userId),
          roleIds: JSON.parse(roleIds),
          tenantId: tenantId
        };
        const alreadyGivenPermissions = await getPermissionByUserAndRole(dispatch, permissionInputs);

        dispatch(setPermissions(alreadyGivenPermissions));
      } else {
        dispatch(
          setError({
            isError: true,
            errorMessage: "Sorry, you don't have permission to access this page",
          })
        );
        setTimeout(() => {
          navigate("/permissionDenied");
        }, 2000);
      }
    }
  };

  const handleSave = async () => {
    if (!roleInfo.id
    ) {
      return;
    }

    if ("Pages.Invoices" in selectedKeys) {
      const extraKeys = [
        "Pages.GetTemplates",
        "Pages.GetCurrencies",
        "Pages.GetIndustries"
      ];

      extraKeys.forEach((key) => {
        selectedKeys[key] = { checked: true, partialChecked: false };
      });
    }


    const input = {
      ...(componentInfo === "Role"
        ? { roleId: roleInfo.id,tenantId:roleInfo?.tenantId }
        : { userId: roleInfo.id, tenantId:roleInfo?.tenantId }),
      permissionNames: selectedKeys ? Object.keys(selectedKeys) : [],
      isGranted: true,
    };
    let updatedPermissions: any = [];

    const selectedNodes = Object.keys(selectedKeys);

    // Check if all selectedNodes exist in permissionsInDB and are granted
    const isUnchanged = selectedNodes.every((permission) => {
      const dbPermission = permissionsInDB.find(
        (node: any) => node.name === permission
      );
      return dbPermission && dbPermission.isGranted === true;
    }) && permissionsInDB?.length === selectedNodes?.length;

    // If no changes, return early without calling the API
    if (isUnchanged) {
      resetDialog();
      return;
    }

    selectedNodes.forEach((permission: any) => {
      if (permissionsInDB?.length === 0) {
        // If permissionsInDB is empty, add all selected keys to uncheckedNodes
        updatedPermissions.push({
          ...(componentInfo === "Role"
            ? { roleId: roleInfo.id, tenantId:roleInfo?.tenantId }
            : { userId: roleInfo.id, tenantId:roleInfo?.tenantId }),
          isGranted: true,
          permissionNames: permission,
        });
      } else {
        // permissionsInDB is not empty; check if permission exists and compare IsGranted status
        const dbPermission = permissionsInDB.find(
          (node: any) => node.PermissionName === permission
        );

        if (dbPermission) {
          // Permission exists in DB, compare IsGranted status
          const selectedIsGranted = (selectedKeys as PermissionCheckState)[
            permission
          ]?.checked;

          if (dbPermission.isGranted !== selectedIsGranted) {
            updatedPermissions.push({
              ...(componentInfo === "Role"
                ? { roleId: roleInfo.id, tenantId: roleInfo?.tenantId }
                : { userId: roleInfo.id, tenantId: roleInfo?.tenantId }),
              isGranted: selectedIsGranted,
              permissionNames: permission,
            });
          }
        } else {
          // Permission is not in permissionsInDB but is in selectedKeys
          updatedPermissions.push({
            ...(componentInfo === "Role"
              ? { roleId: roleInfo.id, tenantId: roleInfo?.tenantId }
              : { userId: roleInfo.id, tenantId: roleInfo?.tenantId }),
            isGranted: (selectedKeys as PermissionCheckState)[permission]
              ?.checked,
            permissionNames: permission,
          });
        }
      }
    });

    // Handle permissions in previouslySelectedNodes that are not in selectedKeys
    Object.keys(previouslySelectedNodes).forEach((dbPermission: any) => {
      if (!selectedKeys.hasOwnProperty(dbPermission)) {
        // If permission is in previouslySelectedNodes but missing in selectedKeys, set IsGranted to false
        updatedPermissions.push({
          ...(componentInfo === "Role"
            ? { roleId: roleInfo.id, tenantId: roleInfo?.tenantId }
            : { userId: roleInfo.id,tenantId: roleInfo?.tenantId }),
          isGranted: false,
          permissionNames: dbPermission,
        });
      }
    });

    const createInput: CreatePermissionByRoleInputDto[] = [...updatedPermissions];
    if (Object.keys(selectedKeys)?.length === 0 && createInput?.length === 0) {
      resetDialog();
      dispatch(showToast({
        severity: 'error',
        summary: 'Error',
        detail: 'Select some permissions to save it!',
        life: 3000
      }));
      return;
    } else if (Object.keys(selectedKeys)?.length !== 0 && createInput?.length === 0) {
      resetDialog();
      return;
    }

    // Transform the array into the required format
    const formattedInput: CreatePermissionInputDto | CreatePermissionByUserInputDto =
      componentInfo === "Role"
        ? {
          roleId: createInput[0].roleId,
          tenantId: createInput[0]?.tenantId,
          permissions: createInput.map(item => ({
            name: String(item.permissionNames),
            isGranted: item.isGranted,
          })),
        }
        : {
          userId: (createInput[0] as any).userId,
          tenantId: createInput[0]?.tenantId,
          permissions: createInput.map(item => ({
            name: String(item.permissionNames),
            isGranted: item.isGranted,
          })),
        };

    try {
      if (componentInfo === "Role") {
        await createPermissionByRoleId(dispatch, formattedInput as CreatePermissionInputDto);
      } else {
        await createPermissionByUserId(dispatch, formattedInput as CreatePermissionByUserInputDto);
      }

      await getPermission();
      resetDialog();

      dispatch(showToast({
        severity: 'success',
        summary: 'Success',
        detail: 'Permission assigned successfully',
        life: 3000
      }));
    } catch (error) {
      console.error('Error assigning permissions:', error);
      dispatch(showToast({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to assign permissions',
        life: 3000
      }));
    }
  };

  const findParentNode = (
    nodes: any,
    childKey: any,
    parentNodes: any[] = []
  ): any[] | null => {
    for (const node of nodes) {
      // If current node has children, continue searching
      if (node.key === childKey) {
        return parentNodes.concat([node]);
      }
      if (node.children) {
        // Check if any child has the specified key
        if (node.children.some((child: any) => child.key === childKey)) {
          // Add the current node (parent) to the parentNodes array
          parentNodes.push(node);
          // Continue searching upwards by calling the function with the current node's key
          findParentNode(tree, node.key, parentNodes);
          return parentNodes; // Return once all parents are found
        }

        // Recursively call the function on children if child key not found at this level
        const result = findParentNode(node.children, childKey, parentNodes);
        if (result) return result; // Return if result is found in deeper recursive calls
      }
    }
    return null;
  };

  const updateCheckedValues = (obj: any): any => {
    for (const key in obj) {
      if (typeof obj[key] === "object" && obj[key] !== null) {
        if ("checked" in obj[key]) {
          obj[key].checked = true;
          obj[key].partialChecked = false;
        }
      }
    }
    Object.keys(obj).forEach((item: any) => {
      const parents = findParentNode(tree, item);
      parents?.forEach((parent) => {
        obj[parent.key] = { checked: true };
      });
    });

    setSelectedKeys({ ...obj });
    return obj;
  };

  const getPermissionOnPageReload = async () => {
    const alreadyGivenPermissions = allPermissions;
    const checkedPermissions = alreadyGivenPermissions?.filter((node: any) => {
      return node.isGranted;
    });
    let obj: any = {};
    checkedPermissions.forEach((permission: any) => {
      obj[permission.name] = { checked: true };
    });
    Object.keys(obj).forEach((item: any) => {
      const parents = findParentNode(tree, item);
      parents?.forEach((parent) => {
        obj[parent.key] = { checked: true };
      });
    });
    setSelectedKeys({ ...obj });
    setPreviouslySelectedNodes({ ...obj });
    setAllUserPermissions({ ...obj });
    setPermissionsInDB(alreadyGivenPermissions);
  };

  useEffect(() => {
    if ((isSaveClicked && roleInfo?.id)
    ) {
      handleSave().finally(() => {
        resetDialog(); // Close the dialog after saving permissions
      });
    }
  }, [isSaveClicked]);

  useEffect(() => {
    if (allPermissions?.length > 0) {
      getPermissionOnPageReload();
    }
  }, [allPermissions]);

  useEffect(() => {
    if (tree) {
      const allExpandedKeys = expandAllNodes(tree);
      setExpandedKeys(allExpandedKeys);
    }
  }, [tree]);

  useEffect(() => {
    if(tenantId===null){
        setTree(treeForHost);
    }else{
      if(edition===1){
        setTree(standardTree);
        return;
      }else if(edition===2){
      setTree(professionalTree);
    }else if(edition===3){
      setTree(enterpriseTree);
    }else{
      setTree(treeForOthers);
    }
  }
    
  }, []);

  return (
    <div className="mb-6">
      <div className="p-0">
        <h3 className="text-lg font-semibold text-gray-800 mb-4 mt-0">
          {componentInfo === "Role" ? "Role Permissions" : "User Permissions"}
        </h3>
        <Tree
          value={tree}
          selectionMode="checkbox"
          selectionKeys={selectedKeys}
          onSelectionChange={(e: any) => {
            updateCheckedValues(e.value);
          }}
          className="w-full"
          expandedKeys={expandedKeys}
          onToggle={(e) => setExpandedKeys(e.value)}
          propagateSelectionUp={false}
          pt={{
            root: { className: 'border border-gray-200 rounded-md p-2' },
            node: { className: 'transition-colors' },
            content: { className: 'p-2 rounded-md hover:bg-gray-100' },
            toggler: { className: 'text-gray-500 mr-2' },
            nodeCheckbox: { className: 'mr-2' },
            nodeIcon: { className: 'text-gray-500 mr-2' },
            subgroup: { className: 'sm:ml-4 sm:mt-1 pl-3 sm:pl-6' }
          }}
        />
        <div className="text-sm text-gray-500 mt-4">
          Select permissions you want to assign to this {componentInfo.toLowerCase()}.
        </div>
      </div>
    </div>
  );
};

export default Permission;